import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import Collapsible from '../../../../library/collapsible/collapsible';
import Tooltip from '../../../../library/tooltip/tooltip';
import { CircleSolidIcon } from '../../../../shared/icons/icons';

import { GET_CANONICAL_SIZES } from '../../../../shared/graphql/product-detail-queries';

import useCanonicalForm from '../../../../pages/product-detail-page/hooks/use-canonical-form/use-canonical-form';
import { useQuery } from '../../../../shared/hooks/hooks';

import {
  ProductProp,
  ProductTypeProp
} from '../../../../shared/utils/prop-types/prop-types';
import { isNotEmptyNullOrUndefined } from '../../../../shared/utils/validators/validators';

import { FILTER_KEYS } from '../../../../shared/constants/filters';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import './size-table.scss';

function SizeTable(props) {
  const {
    isCanonicalSearch,
    onSizeSelected,
    product,
    productType,
    queryFilters,
    selectedFitment,
    storeCode
  } = props;
  const canonicalForm = useCanonicalForm(product);
  const { filters } = canonicalForm;

  const baseProduct = useRef(product?.url?.split('/')[2].split('?')[0]);

  const canonicalProductUrl =
    product?.canonicalFallBackUrl || window.location.pathname;

  const { data: canonicalProductData, isLoading } = useQuery(
    GET_CANONICAL_SIZES,
    {
      variables: {
        id: baseProduct.current,
        productType: productType,
        storeCode: storeCode
      }
    }
  );

  const canonicalProduct = canonicalProductData?.product?.canonicalById || {};

  const sizeOptions =
    productType === PRODUCT_TYPES.TIRES
      ? canonicalProduct?.aggregates?.tireSizes
      : canonicalProduct?.aggregates?.wheelSizes;

  const groupedSizes = sizeOptions?.reduce((k, v) => {
    k[v.diameter] = k[v.diameter] || [];
    k[v.diameter].push(v);
    return k;
  }, {});

  function makeTireSizeRows(data) {
    let filteredData = data.filter(
      (v, i, a) =>
        a.findIndex(
          t => t.width === v.width && t.aspectRatio === v.aspectRatio
        ) === i
    );

    let stackedData = [];
    for (let i = 0; i < filteredData.length / 3; i++) {
      stackedData.push([
        filteredData?.[i * 3],
        filteredData?.[i * 3 + 1],
        filteredData?.[i * 3 + 2]
      ]);
    }
    return stackedData;
  }

  function buildGroupSizeLinks(groupedLinks, product) {
    groupedLinks[product.wheelRimDiameter] =
      groupedLinks[product.wheelRimDiameter] || [];

    if (productType === PRODUCT_TYPES.TIRES) {
      groupedLinks[product.wheelRimDiameter].push({
        code: product.code,
        href: `${canonicalProductUrl}/p/${product.code}`,
        loadIndexSpeedRating: product.loadIndexWithRatingKey || '',
        loadRange: product.loadRange || '',
        size: `${product.tireCrossSection}/${product.tireAspectRatio}-R${product.wheelRimDiameter}`
      });
    } else {
      groupedLinks[product.wheelRimDiameter].push({
        boltPattern: `${product.boltPattern}`,
        code: product.code,
        diameter: `${product.wheelRimDiameter}"`,
        href: `${canonicalProductUrl}/p/${product.code}`,
        width: `${product.wheelWidth}"`
      });
    }

    return groupedLinks;
  }

  const sizeLinkOptions = canonicalProduct?.aggregates?.rangedData?.reduce(
    buildGroupSizeLinks,
    {}
  );

  function getSelectedSizeText() {
    let text = '';

    if (selectedFitment) {
      if (productType === PRODUCT_TYPES.TIRES) {
        text = selectedFitment?.frontTireSize;
      } else {
        text = `${selectedFitment?.frontWheelDiameter}" wheel size`;
      }
    } else {
      if (productType === PRODUCT_TYPES.TIRES) {
        text =
          product?.tireCrossSection &&
          product?.tireAspectRatio &&
          product?.tireWheelRimDiameter
            ? `${product?.tireCrossSection}/${product?.tireAspectRatio}-R${product?.tireWheelRimDiameter}`
            : 'No tire size selected';
      } else {
        text = product?.wheelRimDiameter
          ? `${product?.wheelRimDiameter}" wheel size`
          : 'No wheel size selected';
      }
    }
    return text;
  }

  function getBoltPatternText() {
    let text = '';

    text = product?.boltPatternVal
      ? product?.boltPatternVal
      : 'No bolt pattern selected';

    return text;
  }

  function shouldBeOpen(size) {
    if (productType === PRODUCT_TYPES.TIRES) {
      return size === product?.tireWheelRimDiameter;
    } else {
      return size === product?.wheelRimDiameter;
    }
  }

  function doesMatch(propertiesToMatch) {
    const { code, pattern, size } = propertiesToMatch;

    if (productType === PRODUCT_TYPES.TIRES) {
      if (
        isCanonicalSearch &&
        process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ !== 'true'
      ) {
        const tireCrossSection = queryFilters?.find(
          i => i.name === FILTER_KEYS.CROSS_SECTION
        )?.value;
        const tireAspectRatio = queryFilters?.find(
          i => i.name === FILTER_KEYS.ASPECT_RATIO
        )?.value;
        const tireWheelRimDiameter = queryFilters?.find(
          i => i.name === FILTER_KEYS.RIM_DIAMETER
        )?.value;
        return (
          size ===
          `${tireCrossSection}/${tireAspectRatio}-R${tireWheelRimDiameter}`
        );
      }

      if (process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ === 'true') {
        return code === product?.code;
      } else {
        return (
          size ===
          `${product?.tireCrossSection}/${product?.tireAspectRatio}-R${product?.tireWheelRimDiameter}`
        );
      }
    } else {
      if (
        isCanonicalSearch &&
        process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ !== 'true'
      ) {
        return (
          size ==
            queryFilters?.find(i => i.name === FILTER_KEYS.WHEEL_RIM_DIAMETER)
              ?.value &&
          pattern ===
            queryFilters?.find(i => i.name === FILTER_KEYS.BOLT_PATTERN)?.value
        );
      }

      if (process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ === 'true') {
        return code === product?.code;
      } else {
        return (
          size == product?.wheelRimDiameter?.toString() &&
          pattern === product?.boltPatternVal
        );
      }
    }
  }

  function doesFitSelectedFitment(item) {
    return (
      selectedFitment?.frontWheelDiameter == item.diameter &&
      filters?.boltPattern?.find(i => i.value === item.boltPattern)
    );
  }

  return (
    <div>
      <div styleName="header">
        <div>
          <span>
            Selected Size: <Tooltip content="Selected Size" />
          </span>
          <br />
          <span>{getSelectedSizeText()}</span>
        </div>

        {productType === PRODUCT_TYPES.WHEELS && (
          <div>
            <span>
              Bolt Pattern: <Tooltip content="Bolt Pattern" />
            </span>
            <br />
            <span>{getBoltPatternText()}</span>
          </div>
        )}
      </div>
      {isLoading && !canonicalProductData ? null : (
        <div styleName="collapse-table">
          {canonicalProduct?.aggregates?.codes ? (
            Object.entries(
              process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ === 'true' ||
                process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ === 'true'
                ? sizeLinkOptions
                : groupedSizes
            ).map(size => (
              <div key={size[0]}>
                <Collapsible
                  isOpenByDefault={shouldBeOpen(size[0])}
                  label={`${size[0]}"`}
                  styleName="collapsible-size"
                >
                  {productType === PRODUCT_TYPES.WHEELS ? (
                    <div
                      key={`${size[1]}-inch-options`}
                      styleName={'wheel-table'}
                    >
                      <div>
                        {process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ ===
                        'true' ? (
                          <div styleName="collapse-subtitle">
                            <div>Wheel Size</div>
                            <div>Width</div>
                            <div>Bolt Pattern</div>
                          </div>
                        ) : (
                          <div styleName="collapse-subtitle">
                            <div>Wheel Size</div>
                            <div>Bolt Pattern</div>
                          </div>
                        )}

                        {process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ ===
                        'true' ? (
                          <>
                            {size[1].map(item => {
                              const isMatch = doesMatch({
                                code: item.code
                              });

                              return (
                                <Button
                                  href={isMatch ? null : item.href}
                                  isDisabled={
                                    isMatch ||
                                    !isCanonicalSearch ||
                                    (selectedFitment &&
                                      !doesFitSelectedFitment(item))
                                  }
                                  key={item.code}
                                  onClick={onSizeSelected?.(item)}
                                  styleName={
                                    isMatch
                                      ? 'wheel-button-match'
                                      : 'wheel-button'
                                  }
                                >
                                  <div
                                    styleName={
                                      isMatch
                                        ? 'wheel-diameter-matching'
                                        : 'wheel-diameter'
                                    }
                                  >
                                    {isMatch && (
                                      <CircleSolidIcon styleName="green-dot" />
                                    )}
                                    {item.diameter}
                                  </div>
                                  <div>{item.width}</div>
                                  <div>{item.boltPattern}</div>
                                </Button>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            {size[1].map(item => (
                              <Button
                                isDisabled={
                                  doesMatch({
                                    pattern: item.boltPattern,
                                    size: item.diameter
                                  }) ||
                                  !isCanonicalSearch ||
                                  (selectedFitment &&
                                    !doesFitSelectedFitment(item))
                                }
                                key={item.boltPatternVal}
                                onClick={() => {
                                  window.scrollTo(0, 0);
                                  onSizeSelected({
                                    boltPattern: {
                                      label: item.boltPattern,
                                      value: item.boltPattern
                                    },
                                    wheelRimDiameter: {
                                      label: item.diameter,
                                      value: item.diameter
                                    },
                                    wheelWidth: {
                                      label: item.width,
                                      value: item.width
                                    }
                                  });
                                }}
                                styleName="wheel-button"
                              >
                                <div
                                  styleName={
                                    doesMatch({
                                      pattern: item.boltPattern,
                                      size: item.diameter
                                    })
                                      ? 'wheel-diameter-matching'
                                      : 'wheel-diameter'
                                  }
                                >
                                  {doesMatch({
                                    pattern: item.boltPattern,
                                    size: item.diameter
                                  }) && (
                                    <CircleSolidIcon styleName="green-dot" />
                                  )}
                                  {`${item.diameter}"`}
                                </div>
                                <div styleName="wheel-bolt-pattern">
                                  {item.boltPattern}
                                </div>
                              </Button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={`${size[1]}-inch-options`}
                      styleName={'tire-table'}
                    >
                      <div>
                        <div>
                          {process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ ===
                          'true' ? (
                            <div styleName="collapse-subtitle">
                              <div>Tire Size</div>
                              <div>Load Index/Speed Rating</div>
                              <div>Load Range</div>
                            </div>
                          ) : (
                            <div styleName="collapse-subtitle">Tire Size</div>
                          )}
                        </div>

                        {process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ ===
                        'true' ? (
                          <>
                            {size[1].map(item => {
                              const isMatch = doesMatch({
                                code: item.code
                              });

                              return (
                                <div key={item.code} styleName="tire-rows">
                                  {isNotEmptyNullOrUndefined(item) ? (
                                    <div styleName="tire-items">
                                      <Button
                                        href={isMatch ? null : item.href}
                                        isDisabled={
                                          isMatch ||
                                          !isCanonicalSearch ||
                                          (selectedFitment &&
                                            !doesFitSelectedFitment(item))
                                        }
                                        onClick={onSizeSelected?.(item)}
                                        styleName={
                                          isMatch
                                            ? 'tire-button-match'
                                            : 'tire-button'
                                        }
                                      >
                                        <div>
                                          {isMatch && (
                                            <CircleSolidIcon styleName="green-dot" />
                                          )}
                                          {item.size}
                                        </div>
                                        <div>{item.loadIndexSpeedRating}</div>
                                        <div>{item.loadRange}</div>
                                      </Button>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            {makeTireSizeRows(size[1]).map(row => (
                              <div key={row[0].width} styleName="tire-rows">
                                {row.map(item => {
                                  const tireSize = `${item?.width}/${item?.aspectRatio}-R${item?.diameter}`;
                                  const isMatch = doesMatch({
                                    size: tireSize
                                  });

                                  return isNotEmptyNullOrUndefined(item) ? (
                                    <div key={tireSize} styleName="tire-items">
                                      <Button
                                        isDisabled={
                                          isMatch ||
                                          !isCanonicalSearch ||
                                          (selectedFitment &&
                                            !doesFitSelectedFitment(item))
                                        }
                                        onClick={() => {
                                          window.scrollTo(0, 0);
                                          onSizeSelected({
                                            aspectRatio: {
                                              label: item.aspectRatio,
                                              value: item.aspectRatio
                                            },
                                            crossSection: {
                                              label: item.width,
                                              value: item.width
                                            },
                                            rimDiameter: {
                                              label: item.diameter,
                                              value: item.diameter
                                            }
                                          });
                                        }}
                                        styleName={
                                          isMatch
                                            ? 'tire-button-match'
                                            : 'tire-button'
                                        }
                                      >
                                        {isMatch && (
                                          <CircleSolidIcon styleName="green-dot" />
                                        )}
                                        {tireSize}
                                      </Button>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </Collapsible>
              </div>
            ))
          ) : (
            <Collapsible
              isOpenByDefault
              label={`${
                productType === PRODUCT_TYPES.TIRES
                  ? product.tireWheelRimDiameter
                  : product.wheelRimDiameter
              }"`}
              styleName="collapsible-size"
            >
              {productType === PRODUCT_TYPES.WHEELS ? (
                <div styleName="wheel-table">
                  <div>
                    <div styleName="collapse-subtitle">
                      <div>Wheel Size</div>
                      <div>Bolt Pattern</div>
                    </div>
                    <Button isDisabled styleName="wheel-button">
                      <div styleName="wheel-diameter-matching">
                        <CircleSolidIcon styleName="green-dot" />
                        {`${product.wheelRimDiameter}"`}
                      </div>
                      <div styleName="wheel-bolt-pattern">
                        {product.boltPatternVal}
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                <div styleName="tire-table">
                  <div>
                    <div>
                      <div styleName="collapse-subtitle">Tire Size</div>
                    </div>
                    <div styleName="tire-rows">
                      <div styleName="tire-items">
                        <Button isDisabled styleName="tire-button-match">
                          <CircleSolidIcon styleName="green-dot" />
                          {product?.tireCrossSection}/{product?.tireAspectRatio}
                          -R
                          {product?.tireWheelRimDiameter}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Collapsible>
          )}
        </div>
      )}
    </div>
  );
}

SizeTable.propTypes = {
  isCanonicalSearch: PropTypes.bool,
  onSizeSelected: PropTypes.func,
  product: ProductProp,
  productType: ProductTypeProp.isRequired,
  queryFilters: PropTypes.object,
  selectedFitment: PropTypes.object,
  storeCode: PropTypes.string
};

export default SizeTable;
